import type {
  PartnerSubscriptionRenewalPeriod,
  PartnerSubscriptionStatus,
} from '@/api/partners/subscriptions/types';
import { capitalizeFirstLetter } from '@/core/utils';
import { ChipType } from '@/ui/chip';

const PARTNER_RESERVATION_STATUS_LABELS: Record<string, string> = {
  pending: 'Pending',
  open: 'Booked',
  verified: 'Booked',
  confirmed: 'Confirmed',
  rental: 'Rental',
  cleaning: 'Cleaning',
  completed: 'Completed',
  billable: 'Completed',
  late: 'Late',
  cancelled: 'Cancelled',
  blocked: 'Blocked',
};

export const getPartnerReservationStatusLabel = (
  status: string,
  isBlock = false
): string => {
  if (isBlock) {
    return 'Blocked';
  }

  return (
    PARTNER_RESERVATION_STATUS_LABELS[status] ?? capitalizeFirstLetter(status)
  );
};

const PARTNER_RESERVATION_STATUS_COLORS: Record<string, string> = {
  Pending: '#0085ba',
  Booked: '#0085ba',
  Confirmed: '#00bfb9',
  Rental: '#00c965',
  Cleaning: '#8E4585',
  Completed: '#1b4a00',
  Late: '#ad3700',
  Cancelled: '#ab0300',
  Blocked: '#6b7280',
};

export const getPartnerReservationStatusColor = (
  status: string,
  isBlock = false
): string => {
  const label = getPartnerReservationStatusLabel(status, isBlock);

  return PARTNER_RESERVATION_STATUS_COLORS[label] ?? '#1f1f1f';
};

export const getPartnerReservationStatusChipType = (
  status: string
): ChipType => {
  switch (getPartnerReservationStatusLabel(status)) {
    case 'Booked':
      return ChipType.PENDING;
    case 'Confirmed':
    case 'Rental':
    case 'Completed':
      return ChipType.SUCCESS;
    case 'Cleaning':
      return ChipType.BRAND;
    case 'Late':
      return ChipType.WARNING;
    case 'Cancelled':
      return ChipType.ERROR;
    default:
      return ChipType.DEFAULT;
  }
};

const PARTNER_SUBSCRIPTION_STATUS_LABELS: Record<
  PartnerSubscriptionStatus,
  string
> = {
  pending: 'Pending',
  // Paid, waiting for the subscription to be activated.
  open: 'Open',
  active: 'Active',
  cancelled: 'Cancelled',
};

export const PARTNER_SUBSCRIPTION_STATUSES = Object.keys(
  PARTNER_SUBSCRIPTION_STATUS_LABELS
) as PartnerSubscriptionStatus[];

export const getPartnerSubscriptionStatusLabel = (
  status: PartnerSubscriptionStatus
): string => PARTNER_SUBSCRIPTION_STATUS_LABELS[status];

export const getPartnerSubscriptionStatusChipType = (
  status: PartnerSubscriptionStatus
): ChipType => {
  switch (status) {
    case 'open':
      return ChipType.PENDING;
    case 'active':
      return ChipType.SUCCESS;
    case 'cancelled':
      return ChipType.ERROR;
    default:
      return ChipType.DEFAULT;
  }
};

const RENEWAL_PERIOD_LABELS: Record<PartnerSubscriptionRenewalPeriod, string> =
  {
    weekly: 'Weekly',
    monthly: 'Monthly',
    '6_months': 'Monthly',
  };

export const getRenewalPeriodLabel = (
  renewalPeriod: PartnerSubscriptionRenewalPeriod
): string => RENEWAL_PERIOD_LABELS[renewalPeriod];
